import {
  createClient,
  type FunctionInvokeOptions,
  type SupabaseClient,
} from '@supabase/supabase-js';
import { Monitoring } from '../lib/monitoring';
import { getEdgeFunctionErrorDetails } from './edgeFunctionError';

const {
  VITE_SUPABASE_PROJECT_URL: PROJECT_URL,
  VITE_SUPABASE_ANON_KEY: PROJECT_KEY,
} = import.meta.env;

let supabaseClient: SupabaseClient | undefined;

const getSupabaseClient = () => {
  supabaseClient ??= createClient(PROJECT_URL, PROJECT_KEY);
  return supabaseClient;
};

const SLOW_REQUEST_THRESHOLD_MS = 20000;

let activeOrganizationId: string | undefined;

export const setApiOrganizationId = (organizationId: string | undefined) => {
  activeOrganizationId = organizationId;
};

export const baseApi = {
  get auth() {
    return getSupabaseClient().auth;
  },
  get from() {
    return getSupabaseClient().from.bind(getSupabaseClient());
  },
  functions: {
    invoke: async <T>(
      functionName: string,
      options?: FunctionInvokeOptions,
    ) => {
      const headers: Record<string, string> = {
        ...options?.headers,
      };

      if (activeOrganizationId) {
        headers['x-organization-id'] = activeOrganizationId;
      }

      const start = performance.now();
      const result = await getSupabaseClient().functions.invoke<T>(
        functionName,
        {
          ...options,
          headers,
        },
      );
      const duration = Math.round(performance.now() - start);

      if (result.error) {
        const { status, body } = await getEdgeFunctionErrorDetails(
          result.error,
        );
        const statusTag = String(status ?? 'unknown');

        Monitoring.captureError(result.error, {
          tags: { edgeFunction: functionName, edgeFunctionStatus: statusTag },
          extras: { functionName, status, body },
          fingerprint: ['edge-function-error', functionName, statusTag],
        });
      } else if (duration > SLOW_REQUEST_THRESHOLD_MS) {
        Monitoring.captureSlowRequest(functionName, duration);
      }

      return result;
    },
    // @supabase/supabase-js@2.105.1: functions.invoke only returns a Blob for
    // application/octet-stream and application/pdf — image MIME types
    // (image/png, image/jpeg, etc.) fall through to response.text(), corrupting
    // binary data. This method bypasses the SDK and calls response.blob()
    // directly so any content type works.
    invokeBinary: async (
      functionName: string,
      options?: Pick<FunctionInvokeOptions, 'method' | 'headers'>,
    ): Promise<{ data: Blob | null; error: Error | null }> => {
      const headers: Record<string, string> = {
        apikey: PROJECT_KEY,
        ...options?.headers,
      };

      if (activeOrganizationId) {
        headers['x-organization-id'] = activeOrganizationId;
      }

      const { data: sessionData } = await getSupabaseClient().auth.getSession();
      const token = sessionData.session?.access_token ?? PROJECT_KEY;
      headers['Authorization'] = `Bearer ${token}`;

      try {
        const response = await fetch(
          `${PROJECT_URL}/functions/v1/${functionName}`,
          { method: options?.method ?? 'GET', headers },
        );

        if (!response.ok) {
          return { data: null, error: new Error(`HTTP ${response.status}`) };
        }

        return { data: await response.blob(), error: null };
      } catch (err) {
        return {
          data: null,
          error: err instanceof Error ? err : new Error(String(err)),
        };
      }
    },
  },
};
